import { useState, useEffect, useRef } from "react";
import { useApp } from "../context/AppContext";
import { reportAPI } from "../utils/api";

const BASE = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app");

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Styled date picker — same design as RegisterPatient DobPicker
function DatePicker({ value, onChange }) {
  const today      = new Date();
  const todayYear  = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDay   = today.getDate();
  const selDate    = value ? new Date(value + "T00:00:00") : null;
  const selYear    = selDate ? selDate.getFullYear() : null;
  const selMonth   = selDate ? selDate.getMonth()    : null;
  const selDay     = selDate ? selDate.getDate()     : null;
  const [viewYear,  setViewYear]  = useState(selYear  ?? todayYear);
  const [viewMonth, setViewMonth] = useState(selMonth ?? todayMonth);
  const [calView,   setCalView]   = useState("days");
  const [open,      setOpen]      = useState(false);
  const pickerRef   = useRef(null);
  const yearListRef = useRef(null);

  useEffect(() => {
    if (selYear)        setViewYear(selYear);
    if (selMonth != null) setViewMonth(selMonth);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const h = e => { if (pickerRef.current && !pickerRef.current.contains(e.target)) { setOpen(false); setCalView("days"); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  useEffect(() => {
    if (calView === "monthyear" && yearListRef.current) {
      const el = yearListRef.current.querySelector("[data-active='true']");
      if (el) el.scrollIntoView({ block: "center" });
    }
  }, [calView]);

  const daysInMonth    = (y, m) => new Date(y, m+1, 0).getDate();
  const firstDayOfMonth= (y, m) => new Date(y, m, 1).getDay();
  const isFuture       = (y, m, d) => y > todayYear || (y === todayYear && m > todayMonth) || (y === todayYear && m === todayMonth && d > todayDay);
  const isMonthFuture  = (y, m)    => y > todayYear || (y === todayYear && m > todayMonth);

  const selectDay = (y, m, d) => {
    if (isFuture(y, m, d)) return;
    onChange(`${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`);
    setOpen(false); setCalView("days");
  };
  const prevMonth = () => { if (viewMonth === 0) { setViewYear(y => y-1); setViewMonth(11); } else setViewMonth(m => m-1); };
  const nextMonth = () => { if (!isMonthFuture(viewYear, viewMonth)) { if (viewMonth === 11) { setViewYear(y => y+1); setViewMonth(0); } else setViewMonth(m => m+1); } };

  const firstDay = firstDayOfMonth(viewYear, viewMonth);
  const days     = daysInMonth(viewYear, viewMonth);
  const prevDays = daysInMonth(viewYear, viewMonth === 0 ? 11 : viewMonth-1);
  const cells    = [];
  for (let i = firstDay-1; i >= 0; i--) cells.push({ day: prevDays-i, month: viewMonth === 0 ? 11 : viewMonth-1, year: viewMonth === 0 ? viewYear-1 : viewYear, other: true });
  for (let d = 1; d <= days; d++) cells.push({ day: d, month: viewMonth, year: viewYear, other: false });
  const needed = Math.ceil(cells.length/7)*7; let nd = 1;
  while (cells.length < needed) cells.push({ day: nd++, month: viewMonth === 11 ? 0 : viewMonth+1, year: viewMonth === 11 ? viewYear+1 : viewYear, other: true });
  const years = []; for (let y = todayYear-10; y <= todayYear; y++) years.push(y);

  const displayStr = selDate ? `${String(selMonth+1).padStart(2,"0")} / ${String(selDay).padStart(2,"0")} / ${selYear}` : null;
  const S = {
    wrap:    { position:"relative" },
    input:   { display:"flex", alignItems:"center", width:"100%", background:"#fff", border:`1px solid ${open ? "#4F46E5" : "#CBD5E1"}`, borderRadius:6, padding:"9px 12px", cursor:"pointer", fontSize:13, color:"#1E293B", boxShadow: open ? "0 0 0 2px #EEF2FF" : "0 1px 2px rgba(0,0,0,.06)", userSelect:"none", boxSizing:"border-box", gap:8 },
    popover: { position:"absolute", top:"calc(100% + 6px)", left:0, width:280, background:"#fff", border:"1px solid #E2E8F0", borderRadius:10, boxShadow:"0 10px 40px rgba(0,0,0,.14)", zIndex:999, padding:12, userSelect:"none" },
    header:  { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, padding:"0 4px" },
    monthBtn:{ display:"flex", alignItems:"center", gap:4, fontWeight:700, fontSize:15, color:"#0F172A", background:"none", border:"none", cursor:"pointer", padding:"4px 8px", borderRadius:6 },
    navBtn:  { width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center", background:"none", border:"none", cursor:"pointer", borderRadius:6, color:"#64748B" },
    dayHeader:{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", textAlign:"center", fontSize:11, fontWeight:600, color:"#475569", marginBottom:6 },
    dayGrid: { display:"grid", gridTemplateColumns:"repeat(7,1fr)", textAlign:"center", gap:"2px 1px" },
    yearScroll:{ height:128, overflowY:"auto", background:"#F8FAFC", borderRadius:6, marginBottom:12, border:"1px solid #E2E8F0" },
    monthGrid: { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:4, fontSize:13, textAlign:"center", fontWeight:500 },
  };

  return (
    <div style={S.wrap} ref={pickerRef}>
      <div style={S.input} onClick={() => { setOpen(o => !o); if (!open) setCalView("days"); }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        {displayStr ? <span style={{ flex:1, fontWeight:600, letterSpacing:".04em" }}>{displayStr}</span> : <span style={{ color:"#94A3B8", flex:1 }}>mm / dd / yyyy</span>}
        {value && <span onMouseDown={e => { e.stopPropagation(); onChange(""); setOpen(false); }} style={{ color:"#94A3B8", cursor:"pointer", fontSize:16, lineHeight:1, padding:"0 2px" }}>×</span>}
      </div>
      {open && (
        <div style={S.popover}>
          <style>{`.dp-year-scroll::-webkit-scrollbar{width:8px}.dp-year-scroll::-webkit-scrollbar-track{background:#F1F5F9;border-radius:4px}.dp-year-scroll::-webkit-scrollbar-thumb{background:#CBD5E1;border-radius:4px}`}</style>
          {calView === "days" ? (<>
            <div style={S.header}>
              <button style={S.monthBtn} onClick={() => setCalView("monthyear")}>{MONTHS[viewMonth]} {viewYear}<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg></button>
              <div style={{ display:"flex", gap:4 }}>
                <button style={S.navBtn} onClick={prevMonth}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg></button>
                <button style={{ ...S.navBtn, opacity: !isMonthFuture(viewYear,viewMonth)?1:0.3 }} onClick={nextMonth}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg></button>
              </div>
            </div>
            <div style={S.dayHeader}>{["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <div key={d}>{d}</div>)}</div>
            <div style={S.dayGrid}>
              {cells.map((c,i) => {
                const isSel = !c.other && c.year===selYear && c.month===selMonth && c.day===selDay;
                const fut   = isFuture(c.year, c.month, c.day);
                const isToday = !c.other && c.year===todayYear && c.month===todayMonth && c.day===todayDay;
                return <div key={i} onClick={() => !fut && !c.other && selectDay(c.year,c.month,c.day)}
                  style={{ padding:"6px 2px", borderRadius:4, fontSize:13, cursor:c.other||fut?"default":"pointer", color:isSel?"#fff":c.other||fut?"#CBD5E1":"#1E293B", background:isSel?"#4F46E5":"none", fontWeight:isSel?700:isToday?600:400, border:isToday&&!isSel?"1.5px solid #4F46E5":isSel?"2px solid #1E1B4B":"none", opacity:c.other?0.45:1 }}
                  onMouseEnter={e => { if(!c.other&&!fut&&!isSel) e.currentTarget.style.background="#F1F5F9"; }}
                  onMouseLeave={e => { if(!isSel) e.currentTarget.style.background="none"; }}>{c.day}</div>;
              })}
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", marginTop:14, paddingTop:10, borderTop:"1px solid #F1F5F9", padding:"10px 4px 0" }}>
              <button style={{ background:"none", border:"none", cursor:value?"pointer":"default", color:value?"#4F46E5":"#CBD5E1", fontSize:13, fontWeight:600, padding:0 }} onClick={() => { if(value){onChange("");setOpen(false);} }}>Clear</button>
              <button style={{ background:"none", border:"none", cursor:"pointer", color:"#4F46E5", fontSize:13, fontWeight:600, padding:0 }} onClick={() => { const t=new Date(); selectDay(t.getFullYear(),t.getMonth(),t.getDate()); }}>Today</button>
            </div>
          </>) : (<>
            <div style={{ ...S.header, marginBottom:10 }}>
              <button style={S.monthBtn} onClick={() => setCalView("days")}>{MONTHS[viewMonth]} {viewYear}<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ transform:"rotate(180deg)" }}><path d="M6 9l6 6 6-6"/></svg></button>
            </div>
            <div style={S.yearScroll} ref={yearListRef} className="dp-year-scroll">
              {years.map(y => <div key={y} data-active={y===viewYear?"true":"false"} onClick={() => setViewYear(y)}
                style={{ padding:"7px 16px", fontSize:14, cursor:"pointer", fontWeight:y===viewYear?700:400, color:y===viewYear?"#1E293B":"#475569", background:y===viewYear?"#E2E8F0":"none", borderBottom:"1px solid #F1F5F9" }}
                onMouseEnter={e => { if(y!==viewYear) e.currentTarget.style.background="#F1F5F9"; }}
                onMouseLeave={e => { if(y!==viewYear) e.currentTarget.style.background="none"; }}>{y}</div>)}
            </div>
            <div style={S.monthGrid}>
              {MONTHS.map((m,mi) => { const fut=isMonthFuture(viewYear,mi); const isSel=viewYear===selYear&&mi===selMonth;
                return <div key={m} onClick={() => { if(!fut){setViewMonth(mi);setCalView("days");} }}
                  style={{ padding:"8px 4px", borderRadius:4, cursor:fut?"not-allowed":"pointer", color:isSel?"#fff":fut?"#CBD5E1":"#1E293B", background:isSel?"#4F46E5":"none", fontWeight:isSel?700:400, border:isSel?"2px solid #1E1B4B":"none", opacity:fut?0.4:1 }}
                  onMouseEnter={e => { if(!isSel&&!fut) e.currentTarget.style.background="#F1F5F9"; }}
                  onMouseLeave={e => { if(!isSel) e.currentTarget.style.background="none"; }}>{m}</div>;
              })}
            </div>
          </>)}
        </div>
      )}
    </div>
  );
}
function ageFromDob(dob) {
  if (!dob) return null;
  const birth = new Date(dob + "T00:00:00");
  const today = new Date();
  if (isNaN(birth.getTime()) || birth >= today) return null;
  let years  = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth()    - birth.getMonth();
  if (today.getDate() < birth.getDate()) months--;
  if (months < 0) { years--; months += 12; }
  if (years < 0) return null;
  if (years === 0 && months === 0) {
    const days = Math.floor((today - birth) / (1000 * 60 * 60 * 24));
    return days === 1 ? "1 Day" : `${days} Days`;
  }
  if (years === 0)  return months === 1 ? "1 Month" : `${months} Months`;
  if (years < 3)    return `${years}Y ${months}M`;
  return `${years}Y`;
}

// Category → sub-types structure
const REPORT_SUBTYPES = {
  "Hematology": [
    "CBC / Complete Blood Count",
    "PBF / Blood Film Study",
    "Semen Analysis",
    "ESR",
    "Haemoglobin (Hb)",
    "Blood Grouping & Rh Factor",
    "BT / CT",
    "Circulating Eosinophil Count",
  ],
  "Biochemistry": [
    "Blood Sugar / RBS / FBS",
    "Electrolytes",
    "Ferritin",
    "Vitamin D",
    "AMH",
    "Urine for Protein",
  ],
  "Diabetes": [
    "FBS / Fasting Blood Sugar",
    "HbA1c",
    "OGTT",
    "FBS + 2HABF Combined",
  ],
  "Lipid Profile": [
    "Lipid Profile",
  ],
  "Liver Function": [
    "Full LFT Panel",
    "Serum Bilirubin (Direct & Indirect)",
    "A/G Ratio / Serum Proteins",
  ],
  "Kidney Function": [
    "Full KFT Panel",
    "eGFR",
    "ACR / Urine Albumin",
  ],
  "Hormone": [
    "Thyroid Panel (T3/T4/TSH/FT3/FT4)",
    "Reproductive Hormones",
    "Beta-hCG",
  ],
  "Immunology": [
    "Total IgE",
    "Anti-CCP",
  ],
  "Serology": [
    "CRP + RA Test (Qualitative)",
    "CRP Quantitative / Hs-CRP",
    "ASO Titre",
    "ASO + CRP + RA Combined",
    "Dengue NS1+IgM+IgG",
    "HBsAg + Anti-HCV + HIV + VDRL Screening",
    "HBsAg ELISA",
    "Cross Matching",
    "Kala-azar + Malaria",
    "Malarial Parasite (P.v + P.f)",
    "Widal Test",
    "Triple / Febrile Antigens",
    "H.pylori Antibody",
  ],
  "Coagulation": [
    "PT / INR / Prothrombin Time",
    "APTT",
    "D-Dimer",
    "BT / CT",
  ],
  "Microbiology": [
    "Gram Stain / V/S Study",
    "Z-N Stain / AFB",
    "Tuberculin / MT Test",
    "Skin Scraping",
  ],
  "Urinalysis": [
    "Urine RME / Routine & Microscopic",
    "Urine Pregnancy Test",
    "Ketone Bodies",
  ],
  "Stool Analysis": [
    "Stool Examination",
    "OBT / Occult Blood Test",
  ],
  "Cardiac Markers": [
    "Troponin-I",
    "NT-proBNP",
  ],
  "Tumor Markers": [
    "PSA",
    "AFP",
    "CEA",
    "CA-125",
  ],
  "Pathology": [
    "FNAC / Cytopathology",
  ],
};

// Map sub-type back to parent category for storage
const SUBTYPE_TO_CATEGORY = {};
Object.entries(REPORT_SUBTYPES).forEach(([cat, subs]) => {
  subs.forEach(sub => { SUBTYPE_TO_CATEGORY[sub] = cat; });
});

const PREDEFINED_TYPES = Object.keys(REPORT_SUBTYPES);

// ── Client-side file validation ───────────────────────────────────────────────
const ALLOWED_EXTS  = new Set([".pdf", ".jpg", ".jpeg", ".png", ".bmp", ".tiff", ".webp"]);
const MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

// Browser MIME → expected extension(s)
const MIME_TO_EXT = {
  "application/pdf": [".pdf"],
  "image/jpeg":      [".jpg", ".jpeg"],
  "image/jpg":       [".jpg", ".jpeg"],
  "image/png":       [".png"],
  "image/bmp":       [".bmp"],
  "image/tiff":      [".tiff"],
  "image/webp":      [".webp"],
};

function validateFile(f) {
  if (!f) return "No file selected.";

  // Use the LAST dot segment as the real extension (catches double-ext like .jpg.pdf)
  const nameParts = f.name.toLowerCase().split(".");
  const ext = "." + nameParts[nameParts.length - 1];

  // Size check
  if (f.size > MAX_SIZE_BYTES) return `File too large (${(f.size / 1024 / 1024).toFixed(1)} MB). Maximum is 50 MB.`;

  // Extension check
  if (!ALLOWED_EXTS.has(ext)) return `File type "${ext}" is not allowed. Accepted: PDF, JPG, PNG, BMP, TIFF, WEBP.`;

  // MIME vs extension mismatch (catches renamed files the browser can detect)
  const allowedExtsForMime = MIME_TO_EXT[f.type];
  if (f.type && allowedExtsForMime && !allowedExtsForMime.includes(ext)) {
    return `File looks like a ${f.type.split("/")[1].toUpperCase()} but has a "${ext}" extension. Please use the correct file.`;
  }

  // Double extension check — e.g. "report.jpg.pdf" is suspicious
  if (nameParts.length > 2) {
    const secondExt = "." + nameParts[nameParts.length - 2];
    if (ALLOWED_EXTS.has(secondExt) && secondExt !== ext) {
      return `Suspicious filename "${f.name}" — double extension detected. Please rename the file correctly.`;
    }
  }

  return null; // valid
}



const StatusBadge = ({ status }) => {
  const map = {
    "Normal":       { bg: "#DCFCE7", color: "#15803D" },
    "High":         { bg: "#FEF3C7", color: "#B45309" },
    "Low":          { bg: "#DBEAFE", color: "#1D4ED8" },
    "Critical High":{ bg: "#FEE2E2", color: "#B91C1C" },
    "Critical Low": { bg: "#FEE2E2", color: "#B91C1C" },
    "Abnormal":     { bg: "#FEF3C7", color: "#92400E" },
    "Needs Review":   { bg: "#F1F5F9", color: "#475569" },
    "Informational":  { bg: "#E0F2FE", color: "#0369A1" },
  };
  const s = map[status] || map["Needs Review"];
  return (
    <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>
      {status || "Needs Review"}
    </span>
  );
};

// Minimum expected parameter counts per report type (based on Nashman + Popular/Labaid/Ibn Sina standards)
// Used for completeness check after extraction
const COMPLETENESS_MINIMUMS = {
  "CBC / Complete Blood Count":               9,
  "PBF / Blood Film Study":                   4,
  "Semen Analysis":                          10,
  "ESR":                                      1,
  "Haemoglobin (Hb)":                         1,
  "BT / CT":                                  2,
  "PT / INR / Prothrombin Time":              4,
  "D-Dimer":                                  1,
  "HbA1c":                                    1,
  "OGTT":                                     4,
  "FBS / Fasting Blood Sugar":                1,
  "FBS + 2HABF Combined":                     2,
  "Electrolytes":                             3,
  "Lipid Profile":                            4,
  "Full LFT Panel":                           5,
  "Serum Bilirubin (Direct & Indirect)":      2,
  "A/G Ratio / Serum Proteins":               2,
  "eGFR":                                     1,
  "ACR / Urine Albumin":                      1,
  "Urine for Protein":                        1,
  "Thyroid Panel (T3/T4/TSH/FT3/FT4)":        3,
  "Total IgE":                                1,
  "HBsAg ELISA":                              1,
  "Widal Test":                               4,
  "Dengue NS1+IgM+IgG":                       1,
  "CRP + RA Test (Qualitative)":              1,
  "ASO + CRP + RA Combined":                  3,
  "CRP Quantitative / Hs-CRP":               1,
  "Malarial Parasite (P.v + P.f)":            1,
  "Kala-azar + Malaria":                      1,
  "HBsAg + Anti-HCV + HIV + VDRL Screening":  1,
  "Gram Stain / V/S Study":                   3,
  "Tuberculin / MT Test":                     1,
  "Skin Scraping":                            1,
  "Urine RME / Routine & Microscopic":       20,
  "Urine Pregnancy Test":                     1,
  "Ketone Bodies":                            1,
  "Stool Examination":                       15,
  "OBT / Occult Blood Test":                  1,
  "Troponin-I":                               1,
  "NT-proBNP":                                1,
  "PSA":                                      1,
  "FNAC / Cytopathology":                     1,
};

export default function UploadReport({ patient, onClose, onDone }) {
  const { extractReport, saveReport, user } = useApp();
  const [form, setForm] = useState({
    type: "", lab: "Nashman Institute", labId: "",
    date: new Date().toISOString().split("T")[0], time: "",
  });
  const [customCategoryName, setCustomCategoryName] = useState("");
  const [extractionMethod, setExtractionMethod] = useState("");
  const [selCategory, setSelCategory] = useState(null); // for two-step type selection
  const [typeSearch, setTypeSearch] = useState(""); // search across categories + subtypes
  const [showSearchDrop, setShowSearchDrop] = useState(false); // search dropdown visible
  const [file, setFile]         = useState(null);
  const [dragging, setDragging] = useState(false);
  const [step, setStep]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [extractedReport, setExtracted] = useState(null);
  const [editVals, setEditVals] = useState([]);
  const [fileKey, setFileKey]   = useState(null);      // temp key from /reports/extract
  const [origName, setOrigName] = useState("");         // original filename for save phase
  const [extractedMeta, setExtractedMeta] = useState(null); // type/lab/date returned by extraction

  // Last Lab ID hint
  const [lastLabId, setLastLabId] = useState(null);

  // Lab ID duplicate check state
  const [labIdStatus, setLabIdStatus] = useState(null);
  const [labIdInfo, setLabIdInfo]     = useState(null);
  const debounceRef = useRef(null);

  // Fetch last lab ID on mount
  useEffect(() => {
    import("../utils/api").then(({ excelAPI }) => {
      excelAPI.getLastLabId()
        .then(data => { if (data.last_lab_id) setLastLabId(data.last_lab_id); })
        .catch(() => {});
    });
  }, []);

  // Real-time lab ID duplicate check — debounced 400ms
  useEffect(() => {
    if (!form.labId.trim()) { setLabIdStatus(null); setLabIdInfo(null); return; }
    // Don't show "checking" immediately — wait for debounce to avoid flicker
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLabIdStatus("checking");
      try {
        const token = localStorage.getItem("emr_token");
        const res = await fetch(`${BASE}/reports/check-lab-id?lab_id=${encodeURIComponent(form.labId.trim())}`, {
          headers: token ? { "Authorization": `Bearer ${token}` } : {}
        });
        const data = await res.json();
        if (data.exists) {
          setLabIdStatus("duplicate");
          setLabIdInfo({ patientName: data.patient_name, reportId: data.report_id, patientId: data.patient_id });
        } else {
          setLabIdStatus("ok");
          setLabIdInfo(null);
        }
      } catch {
        setLabIdStatus(null);
      }
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [form.labId]);

  const handleFile = (f) => {
    if (!f) return;
    const err = validateFile(f);
    if (err) {
      setError(err);
      setFile(null);
      // Reset the file input so the same file can be re-selected after fix
      const inp = document.getElementById("pdf-input");
      if (inp) inp.value = "";
      return;
    }
    setError("");
    setFile(f);
  };

  const extract = async () => {
    if (!file) return;
    if (!form.labId.trim()) {
      alert("Please enter a Lab ID before extracting. This is required to save the report.");
      return;
    }
    if (form.type === "Other (custom)" && !customCategoryName.trim()) {
      alert("Please enter a category name for the custom report.");
      return;
    }
    if (labIdStatus === "duplicate") {
      setError(`Lab ID "${form.labId}" already exists. Please use a different Lab ID.`);
      return;
    }
    setLoading(true); setError("");
    try {
      // Phase 1: extract only — nothing written to R2 or DB yet
      const result = await extractReport(patient.id, {
        file, type: form.type === "Other (custom)" ? customCategoryName.trim() : form.type,
        lab: form.lab || "Nashman Institute",
        labId: form.labId,
        date: form.date, time: form.time, patientSex: patient.sex || "Female", patientAge: patient.dob ? ageFromDob(patient.dob) : (patient.age || ""),
      });
      // Store the file key for the save phase
      setFileKey(result.file_key);
      setOrigName(result.original_filename || file.name);
      // Update form with extraction results (auto-detected type/lab/date)
      setExtractedMeta(result);
      setForm(f => ({
        ...f,
        type:  result.report_type || f.type,
        lab:   result.lab         || f.lab,
        labId: result.lab_id      || f.labId,
        date:  result.date        || f.date,
        time:  result.time        || f.time,
      }));
      // Map values for editing — recalculate status for titre format at load time
      const vals = (result.values || []).map(v => {
        let value    = v.value    ?? null;
        let valueText = v.value_text || (v.value !== null && v.value !== undefined ? String(v.value) : "");
        let status   = v.status   || "Needs Review";
        let abnormal = v.abnormal || false;
        // Titre format: 1:80, 1:160 etc. — extract denominator for numeric comparison
        const titreM = valueText && String(valueText).match(/^\s*1\s*[:/]\s*(\d+(?:\.\d+)?)\s*$/);
        if (titreM) {
          value = parseFloat(titreM[1]);
          const rMax = v.ref_max ?? null;
          if (rMax !== null) {
            if (value > rMax)      { status = "High";   abnormal = true;  }
            else                   { status = "Normal"; abnormal = false; }
          }
          // ref_min is always null for titres — no meaningful lower bound
          return {
            name:     v.name     || "",
            value,
            valueText,
            unit:     v.unit     || "",
            refMin:   null,
            refMax:   rMax,
            refText:  v.ref_text || "",
            confidence: v.confidence || "high",
            status,
            abnormal,
          };
        }
        return {
          name:     v.name     || "",
          value,
          valueText,
          unit:     v.unit     || "",
          refMin:   v.ref_min  ?? null,
          refMax:   v.ref_max  ?? null,
          refText:  v.ref_text || "",
          confidence: v.confidence || "high",
          status,
          abnormal,
        };
      });
      setEditVals(vals);
      setExtracted({ type: result.report_type || form.type, lab: result.lab || form.lab });
      setExtractionMethod(result._method || result.extraction_method || "");
      if (result.extraction_error) setError(`Note: ${result.extraction_error}`);

      setStep(2);
    } catch (e) {
      setError(e.message || "Extraction failed");
    } finally {
      setLoading(false);
    }
  };

  const updateVal = (i, field, val) => {
    setEditVals(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: val };
      // Always keep valueText in sync with typed value for smooth backspace editing
      if (field === "value") next[i].valueText = String(val ?? "");
      // Recalculate status when value, refMin or refMax changes
      if (field === "value" || field === "refMin" || field === "refMax") {
        // Titre format: 1:80, 1:160 — extract denominator for numeric comparison
        const titreMatch = field === "value" && typeof val === "string" && val.match(/^\s*1\s*[:/]\s*(\d+(?:\.\d+)?)\s*$/);
        if (titreMatch) {
          const titreVal = parseFloat(titreMatch[1]);
          const rMin = parseFloat(next[i].refMin);
          const rMax = parseFloat(next[i].refMax);
          next[i].value     = titreVal;
          next[i].valueText = val.trim();
          if (!isNaN(rMax)) {
            const isHigh = titreVal > rMax;
            const isLow  = !isNaN(rMin) && titreVal < rMin;
            next[i].abnormal = isHigh || isLow;
            next[i].status   = isHigh ? "High" : isLow ? "Low" : "Normal";
          }
          return next;
        }
        // Also allow intermediate states like "3-", "3-5/" while typing (backspace support)
        const isRangeIntermediate = field === "value" && /^\d+-\d*$/.test((val || "").trim());
        const rangeMatch = field === "value" && /^(\d+)-(\d+)$/.test((val || "").trim());
        if (isRangeIntermediate) {
          // Intermediate state — user is still typing (e.g. "3-" or "3-5")
          // Keep value=null, update valueText, only calculate status when complete
          next[i].value     = null;
          next[i].valueText = val.trim();
          if (rangeMatch) {
            // Complete range — calculate status from upper bound
            const parts = val.trim().split("-");
            const lower = parseFloat(parts[0]);
            const upper = parseFloat(parts[1]);
            const rMin  = parseFloat(next[i].refMin);
            const rMax  = parseFloat(next[i].refMax);
            if (!isNaN(upper) && !isNaN(rMin) && !isNaN(rMax)) {
              const isAbn = upper > rMax || lower < rMin;
              next[i].abnormal = isAbn;
              next[i].status   = isAbn ? "Abnormal" : "Normal";
            } else {
              next[i].status   = "Needs Review";
              next[i].abnormal = false;
            }
          } else {
            // Incomplete range — keep as Needs Review until user finishes typing
            next[i].status   = "Needs Review";
            next[i].abnormal = false;
          }
        } else {
        // ── <N prefix handler (Beta-hCG <5.0 etc.) ───────────────────────────
        const ltMatch = field === "value" && typeof val === "string" && val.match(/^<\s*(\d+(?:\.\d+)?)\s*$/);
        if (ltMatch) {
          const threshold = parseFloat(ltMatch[1]);
          const rMax = parseFloat(next[i].refMax);
          next[i].value     = null;
          next[i].valueText = val.trim();
          next[i].status    = (!isNaN(rMax) && threshold <= rMax) ? "Normal" : "High";
          next[i].abnormal  = !(!isNaN(rMax) && threshold <= rMax);
          return next;
        }
        // ── BT/CT time format handler (e.g. "3 min 25 sec") ─────────────────
        const nameLower = (next[i].name || "").toLowerCase();
        const isBTCT = nameLower.includes("bleeding time") || nameLower.includes("clotting time");
        if (isBTCT && field === "value" && typeof val === "string") {
          const timeM = val.match(/(\d+)\s*min\w*\s*(?:(\d+)\s*sec\w*)?/i);
          if (timeM) {
            const totalSecs = parseInt(timeM[1]) * 60 + parseInt(timeM[2] || 0);
            const rMin = parseFloat(next[i].refMin);
            const rMax = parseFloat(next[i].refMax);
            next[i].value     = totalSecs;
            next[i].valueText = val.trim();
            if (!isNaN(rMin) && !isNaN(rMax)) {
              next[i].status   = totalSecs > rMax ? "High" : totalSecs < rMin ? "Low" : "Normal";
              next[i].abnormal = totalSecs > rMax || totalSecs < rMin;
            }
            return next;
          }
        }
        const num    = parseFloat(field === "value" ? val : next[i].value);
        const rMin   = parseFloat(field === "refMin" ? val : next[i].refMin);
        const rMax   = parseFloat(field === "refMax" ? val : next[i].refMax);
        if (!isNaN(num) && !isNaN(rMin) && !isNaN(rMax)) {
          const isHigh = num > rMax;
          const isLow  = num < rMin;
          next[i].abnormal = isHigh || isLow;
          next[i].status   = isHigh ? "High" : isLow ? "Low" : "Normal";
        } else if (field === "value" && val === "") {
          // User cleared the input — reset both value and valueText
          next[i].value = null;
          next[i].valueText = "";
          next[i].status = "Needs Review";
          next[i].abnormal = false;
        } else if (field === "value" && isNaN(num)) {
          // Text result typed — auto-capitalise known words, then auto-set status
          const CAPITALISE_MAP = {
            "positive": "Positive", "negative": "Negative",
            "reactive": "Reactive", "non-reactive": "Non-Reactive", "non reactive": "Non-Reactive",
            "found": "Found", "not found": "Not Found",
            "present": "Present", "absent": "Absent",
            "detected": "Detected", "not detected": "Not Detected",
            "normal": "Normal", "abnormal": "Abnormal",
            "nil": "Nil", "compatible": "Compatible", "incompatible": "Incompatible",
            "no fungi": "No Fungi", "fungi found": "Fungi Found", "fungi present": "Fungi Present",
            "significant": "Significant", "non-significant": "Non-Significant",
          };
          const formatted = CAPITALISE_MAP[val.trim().toLowerCase()] || val;
          const t = formatted.trim().toLowerCase();
          const ABN_WORDS = ["positive","reactive","found","present","detected","incompatible","significant","fungi","hyphae","spores","+","++","+++"];
          const NRM_WORDS = ["negative","non-reactive","non reactive","not found","not seen","absent","normal","compatible","non-significant","not detected","no fungi","no fungal","no organism","no growth","nil"];
          // Ambiguous/borderline terms — cannot be classified as Normal or Abnormal.
          // equivocal: Dengue IgM/IgG, APS, Lyme borderline zone
          // indeterminate: HIV, HCV repeat-needed results
          // borderline: anti-CCP, HCV, dengue
          // inconclusive: PCR, molecular tests
          // not filled: blank template placeholder (extraction prompt rule 2)
          const AMBIGUOUS_WORDS = ["equivocal","indeterminate","borderline","inconclusive","not filled"];
          // Slash-pair reference legend patterns — both a positive and negative term
          // printed together as a reference column legend, not an actual result.
          const SLASH_PAIRS = [
            "negative/positive","positive/negative",
            "reactive/non-reactive","non-reactive/reactive",
            "reactive/non reactive","non reactive/reactive",
            "detected/not detected","not detected/detected",
            "present/absent","absent/present",
            "found/not found","not found/found",
          ];
          const isAbn = ABN_WORDS.includes(t) || t.startsWith("+") || ABN_WORDS.slice(0,-3).some(w => t.includes(w));
          const isNrm = NRM_WORDS.includes(t) || NRM_WORDS.some(w => t.includes(w));
          const isAmbiguous = AMBIGUOUS_WORDS.some(w => t.includes(w));
          const isSlashPair = SLASH_PAIRS.some(p => t.includes(p));
          next[i].value = null;
          next[i].valueText = formatted;
          // Urine Pregnancy Test: Positive = pregnant, not a pathological abnormality
          const isPregnancyTest = (next[i].name || "").toLowerCase() === "urine pregnancy test";
          // Blood Group / Rh Factor: always Informational — "Positive" in "AB Positive"
          // or "A Positive" is a blood type descriptor, not a clinical finding.
          // Covers all canonical names these params normalize to.
          const INFORMATIONAL_PARAMS = ["blood group", "rh factor", "patient blood group"];
          const isAlwaysInformational = INFORMATIONAL_PARAMS.includes((next[i].name || "").toLowerCase());
          // Negation wins over positive for phrases like "no fungi" / "not found"
          if (isAlwaysInformational)             { next[i].status = "Informational"; next[i].abnormal = false; }
          else if (isSlashPair)                  { next[i].status = "Needs Review";  next[i].abnormal = false; next[i].valueText = "Not filled"; }
          else if (isAmbiguous)                  { next[i].status = "Needs Review";  next[i].abnormal = false; }
          else if (isNrm)                        { next[i].status = "Normal";        next[i].abnormal = false; }
          else if (isAbn && isPregnancyTest) { next[i].status = "Needs Review"; next[i].abnormal = false; }
          else if (isAbn)                    { next[i].status = "Abnormal";     next[i].abnormal = true;  }
          else                               { next[i].status = "Needs Review"; next[i].abnormal = false; }
          // Sync __comment__ for ELISA reports when main result changes
          const commentIdx = next.findIndex(r => r.name === "__comment__");
          if (commentIdx !== -1 && (next[i].name || "").toLowerCase().includes("hbsag")) {
            next[commentIdx].valueText = formatted;
            next[commentIdx].value = null;
          }
        }
        } // close else (non-range path)
      }
      // If status manually changed, sync abnormal flag
      if (field === "status") {
        const _name = (next[i].name || "").toLowerCase();
        const _alwaysInfo = ["blood group", "rh factor", "patient blood group"].includes(_name);
        if (_alwaysInfo) {
          next[i].status   = "Informational";
          next[i].abnormal = false;
        } else {
          next[i].abnormal = !["Normal", "Needs Review", "TNP", "Informational"].includes(val);
        }
      }
      return next;
    });
  };

  const saveAndDone = async () => {
    if (!fileKey) { setError("No file to save. Please go back and re-upload."); return; }
    try {
      setLoading(true);
      // Phase 2: upload file to R2 + write to DB with final reviewed values
      const saved = await saveReport(patient.id, {
        fileKey,
        originalFilename: origName,
        type:   form.type === "Other (custom)" ? customCategoryName.trim() : form.type,
        lab:    form.lab || "Nashman Institute",
        labId:  form.labId,
        date:   form.date,
        time:   form.time,
        extractionMethod,
        values: editVals,
      });
      onDone(saved);
    } catch (e) {
      setError(e.message || "Could not save report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Upload Test Report</div>
            <div style={{ fontSize: 12, color: "#64748B" }}>{patient.name} · {patient.id}</div>
          </div>
          <button className="btn btn-icon btn-ghost" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Steps */}
        <div style={{ padding: "10px 24px 0", display: "flex", gap: 6 }}>
          {["Upload & Extract", "Review Values"].map((s, i) => (
            <div key={s} style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", background: i + 1 <= step ? "#4F46E5" : "#E4E8F0", color: i + 1 <= step ? "#fff" : "#94A3B8", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: i + 1 <= step ? "#4F46E5" : "#94A3B8" }}>{s}</div>
              {i < 1 && <div style={{ flex: 1, height: 2, background: step > 1 ? "#4F46E5" : "#E4E8F0", borderRadius: 99 }} />}
            </div>
          ))}
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-amber">{error}</div>}

          {step === 1 && (
            <div>
              <div className="form-grid-2" style={{ marginBottom: 14 }}>
                <div className="form-group" style={{ gridColumn:"1/-1" }}>
                  <label className="form-label">Report Type</label>

                  {/* ── Professional Master-Detail Upload UI ── */}
                  {(() => {
                    const GROUPS = [
                      { label:"ROUTINE LABS", cats:["Hematology","Biochemistry","Diabetes","Lipid Profile","Liver Function","Kidney Function","Urinalysis","Stool Analysis"] },
                      { label:"SPECIALTY / IMMUNO", cats:["Hormone","Immunology","Serology","Coagulation","Microbiology"] },
                      { label:"ADVANCED & MISC", cats:["Cardiac Markers","Tumor Markers","Pathology"] },
                    ];
                    const q = typeSearch.toLowerCase().trim();
                    const searchResults = q.length < 1 ? [] : Object.entries(REPORT_SUBTYPES).reduce((acc,[cat,subs])=>{
                      const catMatch = cat.toLowerCase().includes(q);
                      const matchedSubs = subs.filter(s=>s.toLowerCase().includes(q));
                      if(catMatch||matchedSubs.length>0) acc.push({cat,subs:catMatch?subs:matchedSubs});
                      return acc;
                    },[]);
                    const hl = (text,q)=>{
                      if(!q) return text;
                      const idx=text.toLowerCase().indexOf(q.toLowerCase());
                      if(idx===-1) return text;
                      return <>{text.slice(0,idx)}<mark style={{background:"#FEF08A",borderRadius:2,padding:"0 1px"}}>{text.slice(idx,idx+q.length)}</mark>{text.slice(idx+q.length)}</>;
                    };
                    const activeCat = selCategory || (form.type&&form.type!=="Other (custom)" ? SUBTYPE_TO_CATEGORY[form.type] : form.type==="Other (custom)"?"Other":null);

                    return (
                      <div style={{display:"flex",height:420,borderRadius:16,border:"1px solid #E2E8F0",overflow:"hidden",background:"#F8FAFC",boxShadow:"0 1px 3px rgba(0,0,0,0.06)"}}>

                        {/* ── LEFT PANE ── */}
                        <div style={{width:220,minWidth:220,borderRight:"1px solid #E2E8F0",display:"flex",flexDirection:"column",background:"#F8FAFC",flexShrink:0}}>

                          {/* Sticky Search */}
                          <div style={{padding:"12px 12px 10px",borderBottom:"1px solid #E2E8F0",background:"rgba(248,250,252,0.95)",backdropFilter:"blur(4px)",flexShrink:0,position:"sticky",top:0,zIndex:10}}>
                            <div style={{position:"relative"}}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2.5" style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                              <input
                                style={{width:"100%",paddingLeft:30,paddingRight:28,paddingTop:7,paddingBottom:7,borderRadius:8,border:"1.5px solid #CBD5E1",fontSize:12.5,fontWeight:500,background:"white",outline:"none",boxSizing:"border-box",color:"#1E293B",boxShadow:"0 1px 2px rgba(0,0,0,0.05)",transition:"border-color 0.15s",fontFamily:"inherit"}}
                                placeholder="Search 17 categories..."
                                value={typeSearch}
                                onChange={e=>{setTypeSearch(e.target.value);setShowSearchDrop(true);}}
                                onFocus={e=>{e.target.style.borderColor="#3B82F6";e.target.style.boxShadow="0 0 0 3px rgba(59,130,246,0.1)";setShowSearchDrop(true);}}
                                onBlur={e=>{e.target.style.borderColor="#CBD5E1";e.target.style.boxShadow="0 1px 2px rgba(0,0,0,0.05)";setTimeout(()=>setShowSearchDrop(false),150);}}
                              />
                              {typeSearch&&<button type="button" onClick={()=>{setTypeSearch("");setShowSearchDrop(false);}} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#94A3B8",fontSize:16,lineHeight:1,padding:0}}>×</button>}
                            </div>
                          </div>

                          {/* Category List */}
                          <div style={{overflowY:"auto",flex:1,padding:"8px 8px 12px"}}>
                            {GROUPS.map(({label,cats})=>(
                              <div key={label}>
                                <div style={{padding:"12px 10px 4px",fontSize:9.5,fontWeight:700,letterSpacing:"0.9px",color:"#94A3B8",textTransform:"uppercase"}}>{label}</div>
                                {cats.map(cat=>{
                                  const isActive=activeCat===cat;
                                  return(
                                    <button key={cat} type="button"
                                      onClick={()=>{setSelCategory(cat);setForm(f=>({...f,type:""}));setTypeSearch("");setShowSearchDrop(false);}}
                                      style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",padding:"7px 10px",border:isActive?"1px solid #BFDBFE":"1px solid transparent",borderRadius:8,background:isActive?"#DBEAFE":"none",cursor:"pointer",textAlign:"left",transition:"all 0.1s",marginBottom:1}}>
                                      <span style={{fontSize:12.5,fontWeight:isActive?700:500,color:isActive?"#1E40AF":"#475569"}}>{cat}</span>
                                      {isActive&&<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="3"><path d="M9 18l6-6-6-6"/></svg>}
                                    </button>
                                  );
                                })}
                              </div>
                            ))}
                            <div style={{padding:"12px 10px 4px",fontSize:9.5,fontWeight:700,letterSpacing:"0.9px",color:"#94A3B8",textTransform:"uppercase"}}>OTHER</div>
                            <button type="button"
                              onClick={()=>{setSelCategory("Other");setForm(f=>({...f,type:"Other (custom)"}));setTypeSearch("");}}
                              style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",padding:"7px 10px",border:activeCat==="Other"?"1px solid #BFDBFE":"1px solid transparent",borderRadius:8,background:activeCat==="Other"?"#DBEAFE":"none",cursor:"pointer",textAlign:"left",marginBottom:1}}>
                              <span style={{fontSize:12.5,fontWeight:activeCat==="Other"?700:500,color:activeCat==="Other"?"#1E40AF":"#475569"}}>Other / Custom</span>
                              {activeCat==="Other"&&<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="3"><path d="M9 18l6-6-6-6"/></svg>}
                            </button>
                          </div>
                        </div>

                        {/* ── RIGHT PANE ── */}
                        <div style={{flex:1,display:"flex",flexDirection:"column",background:"white",overflow:"hidden",position:"relative"}}>

                          {/* Search Dropdown */}
                          {showSearchDrop&&q.length>0&&(
                            <div style={{position:"absolute",top:0,left:0,right:0,zIndex:20,background:"white",borderBottom:"1px solid #E2E8F0",maxHeight:320,overflowY:"auto",boxShadow:"0 4px 16px rgba(0,0,0,0.1)"}}>
                              {searchResults.length===0
                                ?<div style={{padding:"14px 16px",fontSize:12.5,color:"#94A3B8",textAlign:"center"}}>No tests found for "{typeSearch}"</div>
                                :searchResults.map(({cat,subs})=>(
                                  <div key={cat}>
                                    <div style={{padding:"6px 14px 3px",fontSize:9.5,fontWeight:700,letterSpacing:"0.7px",textTransform:"uppercase",color:"#64748B",background:"#F8FAFC",borderBottom:"1px solid #F1F5F9"}}>{hl(cat,q)}</div>
                                    {subs.map(sub=>(
                                      <button key={sub} type="button"
                                        onMouseDown={()=>{setForm(f=>({...f,type:sub}));setSelCategory(cat);setTypeSearch("");setShowSearchDrop(false);setCustomCategoryName("");}}
                                        style={{display:"block",width:"100%",padding:"8px 16px",background:"none",border:"none",textAlign:"left",cursor:"pointer",fontSize:12.5,color:"#1E293B",fontWeight:500,fontFamily:"inherit"}}
                                        onMouseEnter={e=>e.currentTarget.style.background="#F1F5F9"}
                                        onMouseLeave={e=>e.currentTarget.style.background="none"}
                                      >{hl(sub,q)}</button>
                                    ))}
                                  </div>
                                ))
                              }
                            </div>
                          )}

                          {/* No selection */}
                          {!activeCat&&(
                            <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,color:"#CBD5E1",padding:24}}>
                              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 9h18M9 21V9"/></svg>
                              <div style={{fontSize:13,fontWeight:500,color:"#94A3B8"}}>Select a category from the left</div>
                              <div style={{fontSize:11,color:"#CBD5E1"}}>or search above</div>
                            </div>
                          )}

                          {/* Sub-types + dropzone */}
                          {activeCat&&activeCat!=="Other"&&!form.type&&(()=>{
                            const subs=REPORT_SUBTYPES[activeCat]||[];
                            return(
                              <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
                                {/* Sticky right header */}
                                <div style={{padding:"14px 20px 12px",borderBottom:"1px solid #F1F5F9",background:"rgba(255,255,255,0.95)",backdropFilter:"blur(4px)",flexShrink:0,position:"sticky",top:0,zIndex:10}}>
                                  <div style={{fontSize:15,fontWeight:700,color:"#0F172A"}}>{activeCat} Tests</div>
                                  <div style={{fontSize:11.5,color:"#64748B",marginTop:2}}>Select the specific test (optional) and drop the report below.</div>
                                </div>
                                {/* Scrollable body */}
                                <div style={{overflowY:"auto",flex:1,padding:"14px 16px 16px"}}>
                                  {/* Sub-type radio grid */}
                                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:20}}>
                                    {subs.map(sub=>(
                                      <label key={sub} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:12,border:"1.5px solid #E2E8F0",background:"white",cursor:"pointer",transition:"all 0.12s",boxShadow:"0 1px 2px rgba(0,0,0,0.04)"}}>
                                        <input type="radio" name="subtype" value={sub}
                                          onChange={()=>{setForm(f=>({...f,type:sub}));setCustomCategoryName("");}}
                                          style={{accentColor:"#2563EB",width:15,height:15,flexShrink:0}}/>
                                        <span style={{fontSize:12,fontWeight:500,color:"#374151",lineHeight:1.3}}>{sub}</span>
                                      </label>
                                    ))}
                                  </div>

                                  {/* Drop zone */}
                                  <label htmlFor="pdf-input" style={{display:"block",border:"2px dashed #93C5FD",background:"rgba(239,246,255,0.4)",borderRadius:16,padding:"32px 20px",textAlign:"center",cursor:"pointer",transition:"all 0.15s"}}
                                    onMouseEnter={e=>{e.currentTarget.style.background="rgba(239,246,255,0.8)";e.currentTarget.style.borderColor="#60A5FA";}}
                                    onMouseLeave={e=>{e.currentTarget.style.background="rgba(239,246,255,0.4)";e.currentTarget.style.borderColor="#93C5FD";}}
                                    onDragOver={e=>{e.preventDefault();e.currentTarget.style.background="rgba(239,246,255,0.8)";e.currentTarget.style.borderColor="#60A5FA";}}
                                    onDragLeave={e=>{e.currentTarget.style.background="rgba(239,246,255,0.4)";e.currentTarget.style.borderColor="#93C5FD";}}
                                    onDrop={e=>{e.preventDefault();e.currentTarget.style.background="rgba(239,246,255,0.4)";e.currentTarget.style.borderColor="#93C5FD";handleFile(e.dataTransfer.files[0]);}}>
                                    <div style={{width:72,height:72,background:"white",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
                                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>
                                    </div>
                                    <div style={{fontSize:15,fontWeight:800,color:"#0F172A",marginBottom:6}}>Drag & Drop Report Here</div>
                                    <div style={{fontSize:12.5,fontWeight:500,color:"#64748B",marginBottom:16}}>or click to browse files from your computer</div>
                                    <div style={{display:"inline-flex",alignItems:"center",gap:16,fontSize:11.5,fontWeight:700,color:"#94A3B8",background:"white",padding:"6px 16px",borderRadius:99,boxShadow:"0 1px 3px rgba(0,0,0,0.08)",border:"1px solid #E2E8F0"}}>
                                      <span style={{display:"flex",alignItems:"center",gap:5}}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                        PDF
                                      </span>
                                      <span style={{width:3,height:3,borderRadius:"50%",background:"#CBD5E1"}}/>
                                      <span style={{display:"flex",alignItems:"center",gap:5}}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                        JPG / PNG
                                      </span>
                                    </div>
                                  </label>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Selected type */}
                          {form.type&&form.type!=="Other (custom)"&&(
                            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
                              <div style={{padding:"14px 20px 12px",borderBottom:"1px solid #F1F5F9",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
                                <div>
                                  <div style={{fontSize:10,color:"#64748B",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:2}}>{SUBTYPE_TO_CATEGORY[form.type]||"Other"}</div>
                                  <div style={{fontSize:14,fontWeight:700,color:"#0F172A"}}>{form.type}</div>
                                </div>
                                <button type="button" onClick={()=>{setForm(f=>({...f,type:""}));setSelCategory(null);setTypeSearch("");}}
                                  style={{fontSize:12,color:"#2563EB",background:"#EFF6FF",border:"1px solid #BFDBFE",cursor:"pointer",fontWeight:600,padding:"5px 12px",borderRadius:8,fontFamily:"inherit"}}>Change</button>
                              </div>
                              {/* Drop zone after selection */}
                              <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
                                <label htmlFor="pdf-input" style={{display:"block",border:"2px dashed #93C5FD",background:"rgba(239,246,255,0.4)",borderRadius:16,padding:"32px 20px",textAlign:"center",cursor:"pointer",transition:"all 0.15s"}}
                                  onMouseEnter={e=>{e.currentTarget.style.background="rgba(239,246,255,0.8)";e.currentTarget.style.borderColor="#60A5FA";}}
                                  onMouseLeave={e=>{e.currentTarget.style.background="rgba(239,246,255,0.4)";e.currentTarget.style.borderColor="#93C5FD";}}
                                  onDragOver={e=>{e.preventDefault();e.currentTarget.style.background="rgba(239,246,255,0.8)";e.currentTarget.style.borderColor="#60A5FA";}}
                                  onDragLeave={e=>{e.currentTarget.style.background="rgba(239,246,255,0.4)";e.currentTarget.style.borderColor="#93C5FD";}}
                                  onDrop={e=>{e.preventDefault();e.currentTarget.style.background="rgba(239,246,255,0.4)";e.currentTarget.style.borderColor="#93C5FD";handleFile(e.dataTransfer.files[0]);}}>
                                  <div style={{width:72,height:72,background:"white",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
                                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>
                                  </div>
                                  <div style={{fontSize:15,fontWeight:800,color:"#0F172A",marginBottom:6}}>Drag & Drop Report Here</div>
                                  <div style={{fontSize:12.5,fontWeight:500,color:"#64748B",marginBottom:16}}>or click to browse files from your computer</div>
                                  <div style={{display:"inline-flex",alignItems:"center",gap:16,fontSize:11.5,fontWeight:700,color:"#94A3B8",background:"white",padding:"6px 16px",borderRadius:99,boxShadow:"0 1px 3px rgba(0,0,0,0.08)",border:"1px solid #E2E8F0"}}>
                                    <span style={{display:"flex",alignItems:"center",gap:5}}>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                                      PDF
                                    </span>
                                    <span style={{width:3,height:3,borderRadius:"50%",background:"#CBD5E1"}}/>
                                    <span style={{display:"flex",alignItems:"center",gap:5}}>
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                      JPG / PNG
                                    </span>
                                  </div>
                                </label>
                              </div>
                            </div>
                          )}

                          {/* Other custom */}
                          {form.type==="Other (custom)"&&(
                            <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
                              <div style={{padding:"14px 20px 12px",borderBottom:"1px solid #F1F5F9",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
                                <div style={{fontSize:14,fontWeight:700,color:"#0F172A"}}>Other / Custom</div>
                                <button type="button" onClick={()=>{setForm(f=>({...f,type:""}));setSelCategory(null);setCustomCategoryName("");setTypeSearch("");}}
                                  style={{fontSize:12,color:"#2563EB",background:"#EFF6FF",border:"1px solid #BFDBFE",cursor:"pointer",fontWeight:600,padding:"5px 12px",borderRadius:8,fontFamily:"inherit"}}>Change</button>
                              </div>
                              <div style={{padding:"16px 20px"}}>
                                <input className="input" value={customCategoryName}
                                  onChange={e=>setCustomCategoryName(e.target.value)}
                                  placeholder="Enter test name (e.g. IgA, IgG, ANCA, Cortisol...)" />
                              </div>
                            </div>
                          )}

                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="form-grid-2" style={{ marginBottom: 14 }}>
                <div className="form-group">
                  <label className="form-label">Lab / Institute Name</label>
                  <input className="input" value={form.lab} onChange={e => setForm(f => ({ ...f, lab: e.target.value }))} placeholder="Nashman Institute" />
                  <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>Auto-detected from report; override if needed</div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Lab Report ID
                    {lastLabId && (
                      <span style={{ fontSize: 11, color: "#64748B", fontWeight: 400, marginLeft: 8 }}>
                        Last used: <span
                          style={{ color: "#4F46E5", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                          title="Click to copy last lab ID"
                          onClick={() => setForm(f => ({ ...f, labId: lastLabId }))}
                        >{lastLabId}</span>
                      </span>
                    )}
                  </label>
                  <input
                    className={`input ${labIdStatus === "duplicate" ? "input-error" : ""}`}
                    value={form.labId}
                    onChange={e => setForm(f => ({ ...f, labId: e.target.value }))}
                    placeholder="Enter lab ID"
                  />
                  {labIdStatus === "checking" && (
                    <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 3 }}>Checking...</div>
                  )}
                  {labIdStatus === "duplicate" && labIdInfo && (
                    <div style={{ fontSize: 11, color: "#DC2626", marginTop: 3, fontWeight: 600 }}>
                      ⚠ Already exists — report {labIdInfo.reportId} for patient {labIdInfo.patientId} ({labIdInfo.patientName})
                    </div>
                  )}
                  {labIdStatus === "ok" && (
                    <div style={{ fontSize: 11, color: "#16A34A", marginTop: 3 }}>✓ Lab ID is available</div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Test Date</label>
                  <DatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} />
                </div>
              </div>

              <div
                className={`upload-area ${dragging ? "dragging" : ""}`}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
                onClick={() => document.getElementById("pdf-input").click()}
              >
                <input id="pdf-input" type="file" accept=".pdf,image/*" style={{ display: "none" }} onChange={e => { setError(""); handleFile(e.target.files[0]); }} />
                {file ? (
                  <div>
                    <div style={{ marginBottom: 8, display: "flex", justifyContent: "center" }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#4F46E5" }}>{file.name}</div>
                    <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>{(file.size / 1024).toFixed(1)} KB · <strong style={{ color: "#4F46E5" }}>Click to change</strong></div>
                  </div>
                ) : (
                  <div>
                    <div style={{ marginBottom: 10, display: "flex", justifyContent: "center" }}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.5"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#0F172A", marginBottom: 4 }}>Drop PDF or image here</div>
                    <div style={{ fontSize: 12, color: "#64748B" }}>Supports PDF (digital &amp; scanned), JPG, PNG · Max 20MB</div>
                    <div style={{ marginTop: 12 }}><span className="badge badge-blue">Click to browse</span></div>
                  </div>
                )}
              </div>

              {file && (
                <div className="alert alert-blue" style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                  Nashman AI Agent will extract all values automatically — including scanned PDFs. Review before saving.
                </div>
              )}
            </div>
          )}

          {step === 2 && extractedReport && (
            <div>

              {/* Sex mismatch alert */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Extracted Values — {extractedReport.type}</div>
                  <div style={{ fontSize: 12, color: "#64748B" }}>
                    {extractedReport.lab && <span style={{ marginRight: 10 }}>{extractedReport.lab}</span>}
                    Edit any incorrect values before saving
                  </div>
                </div>
                <span className="badge badge-amber">Editable</span>
              </div>

              {editVals.length === 0 ? (
                <div className="alert alert-amber">
                  No values were extracted from this file. This usually means the image quality is too low, the file is not a lab report, or the content could not be read. You can add values manually using the fields below, or go back and upload a clearer file.
                </div>
              ) : (() => {
                // Completeness check — uses AI-detected type first, falls back to staff-selected type
                const _aiType    = extractedReport?.type || "";
                const _staffType = form.type || "";
                const _isOther   = _staffType === "Other" || _staffType === "Custom" || _staffType === "";
                const _checkType = _aiType || _staffType;
                const _minCount  = COMPLETENESS_MINIMUMS[_checkType];
                const _realVals  = editVals.filter(v => v.name !== "__comment__");
                const _count     = _realVals.length;
                const _failedCount = _realVals.filter(v => v.confidence === "failed").length;
                const _isIncomplete = _minCount && _count < _minCount;
                return (<>
                  {/* Generic warning for Other/unknown type */}
                  {_isOther && !_aiType && (
                    <div className="alert alert-blue" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink:0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      External report — please verify all parameters are correctly extracted before saving.
                    </div>
                  )}
                  {/* Completeness warning */}
                  {_isIncomplete && (
                    <div className="alert alert-amber" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink:0 }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                      Only {_count} parameter{_count !== 1 ? "s" : ""} extracted — expected at least {_minCount} for {_checkType}. Please verify all values are present before saving.
                    </div>
                  )}
                  {/* Failed confidence params warning */}
                  {_failedCount > 0 && (
                    <div className="alert alert-red" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink:0 }}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                      {_failedCount} parameter{_failedCount !== 1 ? "s" : ""} could not be extracted (shown in red) — please enter values manually.
                    </div>
                  )}
                </>);
              })()}
              {editVals.length > 0 && (
                <div style={{ maxHeight: 360, overflowY: "auto" }}>
                  {(() => {
                    const isAllQual = editVals.filter(v => v.name !== "__comment__").every(v =>
                      (v.value === null || v.value === undefined || v.value === "") &&
                      !(v.unit || "").trim() && v.refMin == null
                    );
                    return (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={{ width: isAllQual ? "35%" : "25%" }}>Parameter</th>
                        <th style={{ textAlign: "center", width: isAllQual ? "45%" : "20%" }}>Result</th>
                        {!isAllQual && <th style={{ textAlign: "center", width: "12%" }}>Unit</th>}
                        {!isAllQual && <th style={{ textAlign: "center", width: "23%" }}>Reference</th>}
                        <th style={{ textAlign: "center", width: isAllQual ? "20%" : "20%" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editVals.map((v, i) => {
                        // __comment__ = opinion/interpretation — render as special full-width row
                        if (v.name === "__comment__") {
                          return (
                            <tr key={i} style={{ background: "#F8FAFC", borderTop: "2px solid #E2E8F0" }}>
                              <td colSpan={5} style={{ padding: "10px 8px" }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Opinion / Interpretation</div>
                                <input className="input" value={v.valueText ?? ""}
                                  onChange={e => updateVal(i, "value", e.target.value)}
                                  style={{ width: "100%", padding: "6px 10px", fontSize: 13 }}
                                  placeholder="e.g. Positive, Negative, Reactive..." />
                                <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 4 }}>Edit this if you change the result above — this is what shows as the report opinion.</div>
                              </td>
                            </tr>
                          );
                        }
                        const isFailed = v.confidence === "failed";
                        const isLow    = v.confidence === "low";
                        // Zebra stripe: even rows white, odd rows very light grey
                        const zebraBg  = isFailed ? "#FFF5F5" : isLow ? "#FFFBEB" : i % 2 === 0 ? "#ffffff" : "#F8FAFC";
                        const rowBorder = isFailed ? "1px solid #FECACA" : isLow ? "1px solid #FCD34D" : "none";
                        // Qualitative row: no numeric value, no unit, no ref range
                        const unitStr = (v.unit || "").trim();
                        const isQualRow = isAllQual || (
                          (v.value === null || v.value === undefined || v.value === "") && !unitStr && v.refMin == null
                        );
                        return (
                        <tr key={i}
                          style={{ background: zebraBg, outline: rowBorder, transition: "background 0.12s" }}
                          onMouseEnter={e => { if (!isFailed && !isLow) e.currentTarget.style.background = "#EEF2FF"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = zebraBg; }}>
                          <td style={{ fontWeight: 500 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              {v.name}
                              {isLow && <span title="Please verify — AI was uncertain about this extraction" style={{ fontSize: 10, background: "#FEF3C7", color: "#92400E", padding: "1px 6px", borderRadius: 99, cursor: "help", whiteSpace: "nowrap" }}>⚠ Verify</span>}
                              {isFailed && <span title="AI could not extract this value — please enter manually" style={{ fontSize: 10, background: "#FEE2E2", color: "#991B1B", padding: "1px 6px", borderRadius: 99, cursor: "help", whiteSpace: "nowrap" }}>📝 Manual</span>}
                            </div>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <input className="input" value={v.valueText ?? (v.value !== null && v.value !== undefined ? String(v.value) : "")} onChange={e => updateVal(i, "value", e.target.value)}
                              style={{ width: isQualRow ? 200 : 80, textAlign: isQualRow ? "left" : "center", padding: "5px 8px",
                                borderColor: isFailed ? "#FCA5A5" : isLow ? "#FCD34D" : v.abnormal ? "#fca5a5" : undefined,
                                background: isFailed ? "#FFF5F5" : undefined }}
                              placeholder={isFailed ? "Enter value" : ""} />
                          </td>
                          <td style={{ textAlign: "center", fontSize: 12, color: "#64748B" }}>{isQualRow ? "—" : v.unit}</td>
                          <td style={{ textAlign: "center", verticalAlign: "top", paddingTop: 4 }}>
                            {isQualRow ? (
                              <span style={{ color: "#CBD5E1", fontSize: 12 }}>—</span>
                            ) : (() => {
                              // Titre format — show as single cutoff "< 1:80"
                              const isTitreRef = v.refText && /^\s*1\s*[:/]\s*\d+\s*$/.test(v.refText.trim());
                              if (isTitreRef) {
                                return (
                                  <div style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}>
                                      {"< " + v.refText.trim()}
                                    </div>
                                    <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>cutoff</div>
                                  </div>
                                );
                              }
                              // Standard layout: min/max inputs at TOP, refText scrollable BELOW
                              // This prevents long refText from pushing inputs down and misaligning rows
                              return (
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                  {/* Min/Max inputs always at top — aligned with Value input */}
                                  <div style={{ display: "flex", alignItems: "center", gap: 3, justifyContent: "center" }}>
                                    <input className="input" value={v.refMin ?? ""} onChange={e => updateVal(i, "refMin", e.target.value)}
                                      style={{ width: 55, textAlign: "center", padding: "4px 6px", fontSize: 12 }} placeholder="min" />
                                    <span style={{ color: "#94A3B8", fontSize: 11 }}>–</span>
                                    <input className="input" value={v.refMax ?? ""} onChange={e => updateVal(i, "refMax", e.target.value)}
                                      style={{ width: 55, textAlign: "center", padding: "4px 6px", fontSize: 12 }} placeholder="max" />
                                  </div>
                                  {/* refText scrollable below — capped at 3 lines height */}
                                  {v.refText && (
                                    <div style={{
                                      fontSize: 10, color: "#94A3B8", lineHeight: 1.4,
                                      maxHeight: "3.2em", overflowY: "auto",
                                      background: "#F8FAFC", borderRadius: 4,
                                      border: "0.5px solid #E2E8F0",
                                      padding: "3px 6px", textAlign: "left",
                                      whiteSpace: "pre-line",
                                      scrollbarWidth: "thin",
                                    }}>{v.refText.replace(/ \| /g, "\n")}</div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <select value={v.status || "Needs Review"} onChange={e => updateVal(i, "status", e.target.value)}
                              style={{ fontSize: 11, padding: "3px 6px", borderRadius: 5, border: "1px solid #E2E8F0",
                                background: v.status === "Normal" ? "#DCFCE7" : v.status === "High" || v.status === "Abnormal" ? "#FEF3C7" :
                                  v.status === "Low" ? "#DBEAFE" : v.status?.includes("Critical") ? "#FEE2E2" : v.status === "TNP" ? "#F1F5F9" : v.status === "Informational" ? "#E0F2FE" : "#F1F5F9",
                                color: v.status === "Normal" ? "#166534" : v.status === "High" || v.status === "Abnormal" ? "#92400E" :
                                  v.status === "Low" ? "#1E40AF" : v.status?.includes("Critical") ? "#991B1B" : v.status === "TNP" ? "#94A3B8" : v.status === "Informational" ? "#0369A1" : "#475569",
                                fontWeight: 600, cursor: "pointer" }}>
                              {["Normal","High","Low","Critical High","Critical Low","Abnormal","Needs Review","TNP","Informational"].map(s => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step === 2 && (
            <button className="btn btn-secondary" onClick={() => { setStep(1); setSelCategory(null); setForm(f => ({ ...f, type: "" })); setCustomCategoryName(""); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
              Back
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          {step === 1
            ? <>
                {!form.labId.trim() && file && (
                  <span style={{ fontSize: 12, color: "#F59E0B", marginRight: 8 }}>⚠ Lab ID required</span>
                )}
                <button className="btn btn-primary" onClick={extract}
                  disabled={!file || loading || labIdStatus === "duplicate"}
                  style={!form.labId.trim() && file ? { opacity: 0.6 } : {}}>
                {loading ? "Extracting..." : (
                  <>
                    Extract Values
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                  </>
                )}
              </button>
              </>
            : <button className="btn btn-primary" onClick={saveAndDone} disabled={loading}>
                {loading ? "Saving..." : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    Save Report
                  </>
                )}
              </button>
          }
        </div>
      </div>
    </div>
  );
}